<template>
    <GgmsInput v-model="searchTerm" :iconStart="SearchIcon" placeholder="Search"></GgmsInput>

    <div v-for="category in eventsCategories" :key="category">
        <div v-show="filteredEvents.filter((ev) => ev.type === category).length" class="flex flex-col mt-4 gap-y-3">
            <div
                class="w-full bg-primary-color-50 text-primary-color-800 text-xs leading-4 font-semibold uppercase py-1 px-2 rounded-md"
            >
                {{ category }}
            </div>
            <div
                v-for="event in filteredEvents.filter((ev) => ev.type === category)"
                :key="event"
                class="text-gray-900 text-base leading-6 font-medium cursor-pointer"
                @click="chooseEvent(event.title)"
            >
                <TriggerCard
                    :title="convertTriggerEvent(event.title)"
                    :example="event.example"
                    @click="chooseEvent(event.title)"
                />
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
import { computed, ref } from "vue"
import { SearchIcon } from "@heroicons/vue/outline"
import GgmsInput from "@/components/GgmsInput.vue"
import TriggerNode from "@/components/workflows/TriggerNode.vue"
import MembershipChangedTriggerSidebar from "@/components/workflows/sidebar/trigger/MembershipChangedTriggerSidebar.vue"
import PropertyValueChangeTriggerSidebar from "@/components/workflows/sidebar/trigger/PropertyValueChangeTriggerSidebar.vue"
import SubmissionAndViewSidebar from "@/components/workflows/sidebar/trigger/SubmissionAndViewSidebar.vue"
import EnrollAndUnenrollSidebar from "@/components/workflows/sidebar/trigger/EnrollAndUnenrollSidebar.vue"
import TriggerCard from "@/components/workflows/TriggerCard.vue"
import { useWorkflowStore } from "@/stores/workflow"
import { convertTriggerEvent } from "@/shared/utils/helpers"
import { ContactActivityTriggerNodeType, TriggerNodeType } from "@/shared/models/workflow"

const workflowStore = useWorkflowStore()

const searchTerm = ref("")
const categoriesEnum = {
    call: "phone call",
    contact: "contact record",
    form: "form activity",
    collection: "segment membership",
    workflow: "workflow status",
}
const eventsCategories = [
    categoriesEnum.call,
    categoriesEnum.contact,
    categoriesEnum.form,
    categoriesEnum.collection,
    categoriesEnum.workflow,
]

const events = [
    {
        title: ContactActivityTriggerNodeType.callStarted,
        example: "A contact (or your team) initiates an inbound or outbound call—fires the moment the call connects.",
        type: categoriesEnum.call,
    },
    {
        title: ContactActivityTriggerNodeType.callEnded,
        example: "The call disconnects or is completed—useful for post-call automations such as sending a recap task.",
        type: categoriesEnum.call,
    },
    {
        title: ContactActivityTriggerNodeType.contactCreated,
        example: "A brand-new contact record enters the CRM from any source (form fill, import, API, etc.).",
        type: categoriesEnum.contact,
    },
    {
        title: ContactActivityTriggerNodeType.propertyValueChanged,
        example: "A specified field on the contact record changes (e.g., Stage changes from Nurture → Hot).",
        type: categoriesEnum.contact,
    },
    {
        title: ContactActivityTriggerNodeType.submission,
        example: "The contact submits any tracked form on your site or landing pages.",
        type: categoriesEnum.form,
    },
    {
        title: ContactActivityTriggerNodeType.view,
        example: "The contact loads a page containing the form but hasn’t submitted it yet—handy for soft retargeting.",
        type: categoriesEnum.form,
    },
    {
        title: ContactActivityTriggerNodeType.membershipChanged,
        example: "The contact enters or exits being a member of a collection.",
        type: categoriesEnum.collection,
    },
    {
        title: ContactActivityTriggerNodeType.enrolledInWorkflow,
        example: "The contact is added—manually or automatically—to a workflow.",
        type: categoriesEnum.workflow,
    },
    {
        title: ContactActivityTriggerNodeType.unenrollFromWorkflow,
        example: "The contact is removed—manually or automatically—from a workflow.",
        type: categoriesEnum.workflow,
    },
]

const filteredEvents = computed(() =>
    events.filter((event) => convertTriggerEvent(event.title).toLowerCase().includes(searchTerm.value.toLowerCase()))
)

interface Props {
    modelValue: boolean
}
defineProps<Props>()

const emit = defineEmits(["update:modelValue", "changeAction"])

function chooseEvent(event: string) {
    workflowStore.modelValue = event
    switch (event) {
        case ContactActivityTriggerNodeType.membershipChanged:
            emit("changeAction", { componentName: MembershipChangedTriggerSidebar.__name })
            break
        case ContactActivityTriggerNodeType.propertyValueChanged:
            emit("changeAction", { componentName: PropertyValueChangeTriggerSidebar.__name })
            break
        case ContactActivityTriggerNodeType.submission:
            workflowStore.isSubmission = true
            emit("changeAction", { componentName: SubmissionAndViewSidebar.__name })
            break
        case ContactActivityTriggerNodeType.view:
            workflowStore.isSubmission = false
            emit("changeAction", { componentName: SubmissionAndViewSidebar.__name })
            break
        case ContactActivityTriggerNodeType.enrolledInWorkflow:
            workflowStore.isEnrollWorkflow = true
            emit("changeAction", { componentName: EnrollAndUnenrollSidebar.__name })
            break
        case ContactActivityTriggerNodeType.unenrollFromWorkflow:
            workflowStore.isEnrollWorkflow = false
            emit("changeAction", { componentName: EnrollAndUnenrollSidebar.__name })
            break
        default:
            emit("changeAction", { componentName: TriggerNode.__name, node: TriggerNodeType.contactActivity })
            break
    }
    workflowStore.setHeaderText()
}
</script>
